<template>
  <div class="report-page">
    <ReportHeaderTab />

    <!-- Page Header -->
    <div class="page-header">
      <div class="page-title-wrap">
        <router-link :to="$url('/customer-report')" class="back-link">
          <font-awesome-icon icon="angle-left" class="back-icon" />
          Back
        </router-link>
        <div>
          <h5 class="page-title">{{ customerName }}</h5>
          <p class="page-sub">Customer sales history and transaction detail</p>
        </div>
      </div>

      <div class="filters-wrap">

        <!-- From -->
        <div class="filter-group">
          <div class="filter-label">From</div>
          <div class="date-input-wrap">
            <font-awesome-icon icon="calendar" class="date-icon" />
            <Datepicker v-model="tableData.start_date" :enableTimePicker="false" class="date-picker" />
          </div>
        </div>

        <div class="filter-sep">—</div>

        <!-- To -->
        <div class="filter-group">
          <div class="filter-label">To</div>
          <div class="date-input-wrap">
            <font-awesome-icon icon="calendar" class="date-icon" />
            <Datepicker
              v-model="tableData.end_date"
              :minDate="tableData.start_date"
              :enableTimePicker="false"
              class="date-picker"
            />
          </div>
        </div>

        <!-- Search -->
        <div class="filter-group">
          <div class="filter-label">Search</div>
          <div class="search-wrap">
            <font-awesome-icon icon="search" class="search-icon" />
            <input
              type="text"
              class="search-input"
              placeholder="Search sales..."
              v-model="query"
            />
          </div>
        </div>

        <!-- Apply -->
        <button class="btn-apply" :disabled="crudLoading" @click="fetchData()">
          <font-awesome-icon :icon="crudLoading ? 'spinner' : 'filter'" :spin="crudLoading" />
          {{ crudLoading ? 'Applying...' : 'Apply' }}
        </button>

        <!-- Export -->
        <div class="dropdown">
          <button class="btn-outline" @click="exportOpen = !exportOpen">
            <font-awesome-icon icon="download" /> Export
          </button>
          <ul class="dropdown-menu" :class="{ show: exportOpen }">
            <li>
              <a class="dropdown-item" href="#" @click.prevent="exportPdf">
                <font-awesome-icon icon="file-pdf" class="text-danger me-2" /> PDF
              </a>
            </li>
            <li>
              <a class="dropdown-item" href="#" @click.prevent="exportCsv">
                <font-awesome-icon icon="file-csv" class="text-success me-2" /> CSV
              </a>
            </li>
          </ul>
        </div>

      </div>
    </div>

    <!-- Summary cards -->
    <div class="summary-grid" v-if="details.length > 0 && !loading">
      <div class="summary-card">
        <div class="summary-icon summary-icon--blue">
          <font-awesome-icon icon="receipt" />
        </div>
        <div>
          <div class="summary-label">Total Transactions</div>
          <div class="summary-value">{{ pagination.total }}</div>
        </div>
      </div>
      <div class="summary-card">
        <div class="summary-icon summary-icon--green">
          <font-awesome-icon icon="coins" />
        </div>
        <div>
          <div class="summary-label">Total Amount</div>
          <div class="summary-value">
            {{ company?.currency?.symbol }}{{ $filters.formatMoney(total_amount) }}
          </div>
        </div>
      </div>
    </div>

    <!-- Data card -->
    <div class="data-card">

      <!-- Loading -->
      <div v-if="loading" class="state-block">
        <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
        <p class="mt-3 text-muted-cell">Loading sales data...</p>
      </div>

      <!-- Empty -->
      <div v-else-if="details.length === 0" class="state-block">
        <div class="state-icon">
          <font-awesome-icon icon="receipt" />
        </div>
        <p class="state-title">No sales found</p>
        <p class="state-sub">
          {{ query ? 'No results match your search.' : 'This customer has no sales in the selected date range.' }}
        </p>
      </div>

      <template v-else>

        <!-- Toolbar -->
        <div class="table-toolbar">
          <span class="table-count">
            Showing <strong>{{ pagination.from }}–{{ pagination.to }}</strong>
            of <strong>{{ pagination.total }}</strong> transactions
          </span>
          <div class="toolbar-right">
            <label class="table-count">Rows:</label>
            <select v-model="tableData.length" @change="tableData.page = 1; fetchData()" class="rows-select">
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="30">30</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option :value="pagination.total">All</option>
            </select>
          </div>
        </div>

        <!-- Table -->
        <div class="table-wrap">
          <table class="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Sales Number</th>
                <th>Date</th>
                <th>Time</th>
                <th class="text-right">Total ({{ company?.currency?.symbol }})</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(sale, index) in details" :key="sale.id">

                <td class="text-muted-cell">{{ pagination.from + index }}</td>

                <td>
                  <router-link
                    :to="$url('/sales-receipt-credit/' + sale.id)"
                    class="batch-link"
                  >
                    <span class="batch-code">{{ sale.batch_code }}</span>
                    <font-awesome-icon icon="external-link-alt" class="link-icon" />
                  </router-link>
                </td>

                <td class="text-muted-cell" style="white-space:nowrap;">{{ dateTime(sale.date) }}</td>

                <td class="text-muted-cell">{{ timeConvert(sale.time) }}</td>

                <td class="text-right fw-bold">
                  {{ $filters.formatMoney(sale.total_amount) }}
                </td>

              </tr>
            </tbody>

            <tfoot>
              <tr class="grand-total-row">
                <td colspan="4" class="grand-label">Grand Total</td>
                <td class="text-right fw-bold">{{ $filters.formatMoney(total_amount) }}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <!-- Pagination -->
        <div class="table-footer">
          <pagination
            :pagination="pagination"
            @prev="tableData.page = pagination.currentPage - 1; fetchData()"
            @next="tableData.page = pagination.currentPage + 1; fetchData()"
          />
        </div>

      </template>
    </div>

  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';
import { reusableMixin } from '../../../lib/reusableMixin';
import ReportHeaderTab from './components/reportheadertab.vue';

export default {
  name: 'UserCustomerReport',

  components: { ReportHeaderTab },

  mixins: [reusableMixin],

  data() {
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

    return {
      print_content:     [],
      headerArrayColumn: ['S/N', 'Sales Number', 'Date', 'Time', 'Total'],
      footerRowArray:    [],

      details:     [],
      loading:     false,
      crudLoading: false,
      exportOpen:  false,
      query:       null,

      tableData: { page: 1,
        store:      this.$route.query.store ?? 'All',
        start_date: sixMonthsAgo,
        end_date:   new Date(),
        draw:       0,
        length:     10,
        search:     '',
        column:     0,
        dir:        'desc',
        query:      '',
      },

      pagination: {
        lastPage: '', currentPage: '', total: '',
        lastPageUrl: '', nextPageUrl: '', prevPageUrl: '',
        from: 1, to: '',
      },

      total_amount: 0,
      toast: useToast(),
    };
  },

  computed: {
    customerName() {
      return this.details[0]?.customer?.customer_name ?? 'Customer Detail';
    },
  },

  methods: {
    // ── Helpers ─────────────────────────────────────────────────────────
    timeConvert(time) {
      if (!time) return '—';
      const match = time.toString().match(/^([01]\d|2[0-3])(:)([0-5]\d)(:[0-5]\d)?$/) || [time];
      if (match.length > 1) {
        const parts = match.slice(1);
        parts[5] = +parts[0] < 12 ? 'AM' : 'PM';
        parts[0] = +parts[0] % 12 || 12;
        return parts.join('');
      }
      return time;
    },

    // ── Export ──────────────────────────────────────────────────────────
    exportPdf() {
      this.exportOpen = false;
      this.drawPdf(
        this.print_content, this.headerArrayColumn,
        'l', `${this.customerName}-report`, 'Customer Sales Detail',
      );
    },

    exportCsv() {
      this.exportOpen = false;
      this.reusableMixinToCSV(`${this.customerName}-report.csv`);
    },

    // ── Data ────────────────────────────────────────────────────────────
    grandTotal(items) {
      this.total_amount = items.reduce((s, t) => s + parseFloat(t.total_amount || 0), 0);
    },

    dataToPrint(details) {
      this.print_content = details.map((s, idx) => ({
        sn:         idx + 1,
        batch_code: s.batch_code,
        date:       s.date,
        time:       s.time,
        total:      this.$filters.formatMoney(s.total_amount),
      }));
    },

    fetchData(url = `/report/customer-report/${this.$route.params.customerId}`) {
      this.loading     = true;
      this.crudLoading = true;
      this.tableData.query = this.query;
      this.tableData.draw++;

      axios.get(url, { params: this.tableData })
        .then(({ data }) => {
          this.details = data.data.data;
          this.grandTotal(this.details);
          this.dataToPrint(this.details);
          this.configPagination(data.data);
        })
        .catch(() => {
          this.toast.error('Failed to load customer sales data. Please try again.');
        })
        .finally(() => {
          this.loading     = false;
          this.crudLoading = false;
        });
    },

    configPagination(data) {
      this.pagination.lastPage    = data.last_page;
      this.pagination.currentPage = data.current_page;
      this.pagination.total       = data.total;
      this.pagination.lastPageUrl = data.last_page_url;
      this.pagination.nextPageUrl = data.next_page_url;
      this.pagination.prevPageUrl = data.prev_page_url;
      this.pagination.from        = data.from;
      this.pagination.to          = data.to;
    },
  },

  watch: {
    query() { this.fetchData(); },
  },

  created() {
    this.fetchData();
    this.getCompany();
  },
};
</script>

<style scoped>
/* ── Page ────────────────────────────────────────────────────────────── */
.report-page { display:flex; flex-direction:column; gap:18px; }

/* ── Header ──────────────────────────────────────────────────────────── */
.page-header     { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; }
.page-title-wrap { display:flex; align-items:flex-start; gap:14px; }
.page-title      { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub        { font-size:13px; color:#7a90b0; margin:0; }

.back-link { display:inline-flex; align-items:center; gap:6px; font-size:13px; font-weight:600; color:#7a90b0; cursor:pointer; padding:6px 10px; border-radius:7px; border:1px solid #e4eaf2; background:#fff; transition:all .15s; white-space:nowrap; align-self:flex-start; margin-top:2px; text-decoration:none; }
.back-link:hover { background:#f4f7fb; color:#1a2b45; text-decoration:none; }
.back-icon { font-size:12px; }

/* ── Filters ─────────────────────────────────────────────────────────── */
.filters-wrap     { display:flex; align-items:flex-end; flex-wrap:wrap; gap:10px; }
.filter-group     { display:flex; flex-direction:column; gap:4px; }
.filter-label     { font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; }
.filter-sep       { color:#b0bdd0; font-size:16px; margin-bottom:4px; }
.date-input-wrap  { position:relative; display:flex; align-items:center; }
.date-icon        { position:absolute; left:10px; color:#7a90b0; font-size:12px; pointer-events:none; z-index:1; }
.date-picker      { background:none; border:none; padding-left:28px; }
.search-wrap      { position:relative; display:flex; align-items:center; }
.search-icon      { position:absolute; left:11px; color:#7a90b0; font-size:13px; pointer-events:none; }
.search-input     { padding:8px 12px 8px 33px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; width:200px; outline:none; transition:border-color .15s; }
.search-input:focus { border-color:#004AAD; }
.btn-apply        { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:8px 16px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; white-space:nowrap; }
.btn-apply:hover:not(:disabled) { background:#003b8e; }
.btn-apply:disabled { opacity:.55; cursor:not-allowed; }
.btn-outline      { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:8px 14px; border-radius:8px; cursor:pointer; transition:background .15s; white-space:nowrap; }
.btn-outline:hover { background:#f4f7fb; }
.dropdown         { position:relative; }
.dropdown-menu    { position:absolute; top:calc(100% + 6px); right:0; background:#fff; border:1px solid #e4eaf2; border-radius:10px; box-shadow:0 8px 24px rgba(0,0,0,.1); padding:6px; min-width:160px; z-index:200; display:none; }
.dropdown-menu.show { display:block; }
.dropdown-item    { display:flex; align-items:center; gap:8px; padding:8px 12px; font-size:13px; border-radius:7px; color:#1a2b45; cursor:pointer; text-decoration:none; }
.dropdown-item:hover { background:#f4f7fb; }
.me-2 { margin-right:8px; }

/* ── Summary cards ───────────────────────────────────────────────────── */
.summary-grid        { display:grid; grid-template-columns:repeat(2,1fr); gap:14px; max-width:460px; }
.summary-card        { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:18px 20px; display:flex; align-items:center; gap:16px; }
.summary-icon        { width:44px; height:44px; border-radius:12px; display:flex; align-items:center; justify-content:center; font-size:18px; flex-shrink:0; }
.summary-icon--blue  { background:rgba(0,74,173,.1); color:#004AAD; }
.summary-icon--green { background:#f0fdf4; color:#16a34a; }
.summary-label       { font-size:12px; font-weight:600; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; margin-bottom:5px; }
.summary-value       { font-size:20px; font-weight:700; color:#1a2b45; }

/* ── Data card ───────────────────────────────────────────────────────── */
.data-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }

/* ── States ──────────────────────────────────────────────────────────── */
.state-block { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
.state-icon  { width:60px; height:60px; border-radius:16px; background:rgba(0,74,173,.08); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
.state-title { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
.state-sub   { font-size:13px; color:#7a90b0; margin:0; }
.text-muted-cell { color:#4a5e78; }
.mt-3 { margin-top:12px; }

/* ── Table toolbar ───────────────────────────────────────────────────── */
.table-toolbar  { display:flex; align-items:center; justify-content:space-between; padding:14px 20px; border-bottom:1px solid #e4eaf2; flex-wrap:wrap; gap:10px; }
.table-count    { font-size:13px; color:#7a90b0; }
.table-count strong { color:#1a2b45; }
.toolbar-right  { display:flex; align-items:center; gap:8px; }
.rows-select    { border:1px solid #dde5f0; border-radius:7px; padding:5px 10px; font-size:13px; color:#1a2b45; background:#f8fafc; outline:none; cursor:pointer; }

/* ── Table ───────────────────────────────────────────────────────────── */
.table-wrap  { overflow-x:auto; }
.data-table  { width:100%; border-collapse:collapse; }
.data-table thead tr { background:#f8fafc; }
.data-table thead th { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:12px 16px; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
.data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .12s; }
.data-table tbody tr:last-child { border-bottom:none; }
.data-table tbody tr:hover { background:#f8fafc; }
.data-table tbody td { padding:12px 16px; font-size:13px; vertical-align:middle; }
.text-right { text-align:right; }
.fw-bold    { font-weight:700; }

/* ── Batch link ──────────────────────────────────────────────────────── */
.batch-link  { display:inline-flex; align-items:center; gap:7px; text-decoration:none; }
.batch-link:hover .batch-code { background:#e8f0fb; border-color:#004AAD; }
.batch-code  { font-family:monospace; font-size:12px; font-weight:600; color:#004AAD; background:#edf4ff; border:1px solid #c5d6f5; border-radius:6px; padding:2px 8px; transition:all .15s; }
.link-icon   { font-size:11px; color:#7a90b0; }

/* ── Grand total ─────────────────────────────────────────────────────── */
.grand-total-row    { background:#f8fafc; border-top:2px solid #e4eaf2; }
.grand-total-row td { padding:13px 16px; font-size:13px; }
.grand-label        { font-size:12px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; }

/* ── Footer ──────────────────────────────────────────────────────────── */
.table-footer { padding:14px 20px; border-top:1px solid #e4eaf2; display:flex; justify-content:flex-end; }

/* ── Responsive ──────────────────────────────────────────────────────── */
@media (max-width:768px) {
  .page-header, .page-title-wrap, .filters-wrap { flex-direction:column; align-items:stretch; }
  .filter-sep   { display:none; }
  .search-input { width:100%; }
  .summary-grid { grid-template-columns:1fr; max-width:100%; }
}
</style>